import api from "../../../api";

const BASE = "/api/error-reports";

/**
 * Board / group / priority / status options for the report form.
 * Read live from Monday so a group or label added on the board shows up here
 * without a deploy. Pass a boardId when the user changes the board dropdown.
 */
export const getMondayOptions = (boardId) =>
  api
    .get(`${BASE}/monday/options/`, {
      params: boardId ? { board_id: boardId } : undefined,
    })
    .then((r) => r.data);

/**
 * Submit a report. Multipart, because it carries the screenshots.
 * The shared axios instance defaults to "application/json", so this must
 * clear that default rather than replace it with a hardcoded
 * "multipart/form-data" string: a manually-set Content-Type has no boundary
 * param, and once it's set explicitly the browser won't add one itself,
 * which breaks Django's multipart parser whenever files are attached.
 * Setting it to `undefined` removes the header entirely so the browser
 * computes the correct "multipart/form-data; boundary=..." on its own.
 */
export const createErrorReport = ({
  title,
  description,
  priority,
  status,
  boardId,
  groupId,
  assigneeId,
  pageUrl,
  browserInfo,
  files = [],
}) => {
  const fd = new FormData();
  fd.append("title", title);
  fd.append("description", description);
  if (priority) fd.append("priority", priority);
  if (status) fd.append("monday_status_label", status);
  if (boardId) fd.append("monday_board_id", boardId);
  if (groupId) fd.append("monday_group_id", groupId);
  if (assigneeId) fd.append("monday_assignee_id", assigneeId);
  if (pageUrl) fd.append("page_url", pageUrl);
  // Multipart cannot carry a nested object, so this goes over as JSON text.
  fd.append("browser_info", JSON.stringify(browserInfo || {}));
  files.forEach((f) => fd.append("files", f));

  return api
    .post(`${BASE}/reports/`, fd, {
      headers: { "Content-Type": undefined },
      // Screenshots on a slow connection need longer than the default.
      timeout: 120000,
    })
    .then((r) => r.data);
};

export const getErrorReports = (params) =>
  api.get(`${BASE}/reports/`, { params }).then((r) => r.data);

export const getErrorReport = (id) =>
  api.get(`${BASE}/reports/${id}/`).then((r) => r.data);

/** Correct a report's own title/description/priority. Reporter only. */
export const updateErrorReport = (id, { title, description, priority }) =>
  api
    .patch(`${BASE}/reports/${id}/`, { title, description, priority })
    .then((r) => r.data);

/** Re-send a report that failed to reach Monday. Admin/staff only. */
export const retryErrorReport = (id) =>
  api.post(`${BASE}/reports/${id}/retry/`).then((r) => r.data);

/** Soft-delete a report (and archive its Monday item). cg@collings.com.au only. */
export const deleteErrorReport = (id) => api.delete(`${BASE}/reports/${id}/`);

/** What the browser was doing, so nobody has to ask "which page?" later. */
export const collectBrowserInfo = () => {
  try {
    return {
      browser: navigator.userAgent,
      viewport: `${window.innerWidth} x ${window.innerHeight}`,
      screen: `${window.screen?.width} x ${window.screen?.height}`,
      language: navigator.language,
      platform: navigator.platform,
    };
  } catch {
    return {};
  }
};
